import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AppBar, Toolbar, useMediaQuery, useTheme, Box } from "@mui/material";
import { debounce } from "lodash";
import { DefaultLayoutZIndex } from "../../../../../_utils/constraints";
import MenuList from "../LeftNavBar/MenuList";
import Logo from "../../../../_default/Logo";
import { useScreenSize } from "../../../../_context/ScreenSizeProvider";
import { useLayout } from "../../../../_context/DefaultProvider";
const AnimatedBox = motion(Box); // Enhance Box with motion capabilitie
// Convert AppBar to a motion component
const MotionAppBar = motion(AppBar);

const MainComponent = ({}) => {
  const { navHeight, setVisibility, visibility } = useLayout();

  const lastScrollY = useRef(0);
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const difference = currentScrollY - lastScrollY.current;

    const newVisibility = {
      ...visibility,
      isFixed: currentScrollY > navHeight,
      showAppBar: currentScrollY <= navHeight,
    };

    if (currentScrollY > navHeight) {
      if (difference > 10) {
        newVisibility.showAppBar = false;
      } else if (difference < -10) {
        newVisibility.showAppBar = true;
      }
    }

    setVisibility(newVisibility);
    lastScrollY.current = currentScrollY;
  }, [navHeight, visibility]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 100);
    window.addEventListener("scroll", debouncedHandleScroll);

    return () => {
      debouncedHandleScroll.cancel(); // Ensure debounce cleanup
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [handleScroll]);
  return <TopNavBar />;
};

const TopNavBar = ({}) => {
  const { navHeight, visibility, setNavHeight, drawerStates } = useLayout();
  const { palette } = useTheme();
  const { width } = useScreenSize();
  const navRef = useRef(null);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const isIcon = useMediaQuery((theme) => theme.breakpoints.up("xxl"));

  // Update the height of the AppBar using ResizeObserver
  useEffect(() => {
    const appBarElement = navRef.current;
    if (!appBarElement) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === appBarElement) {
          if (navHeight !== entry.contentRect.height) {
            setNavHeight(entry.contentRect.height);
          }
        }
      }
    });

    resizeObserver.observe(appBarElement);

    // Initial calculation
    setNavHeight(appBarElement.offsetHeight);

    // Cleanup observer on unmount
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box
      style={{
        position: "relative",
        backgroundColor: "transparent",
        padding: 0,
        margin: 0,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <MotionAppBar
        ref={navRef}
        initial={{ opacity: 0, translateY: 0 }} // Start with full opacity and at the normal position
        animate={{
          opacity: visibility.showAppBar
            ? !drawerStates?.isLeftMenuOpen
              ? 1
              : 0
            : 0, // Animate opacity based on visibility
          translateY: visibility.showAppBar
            ? !drawerStates?.isLeftMenuOpen
              ? 0
              : -navHeight
            : -navHeight, // Move out of view when hidden
        }}
        exit={{ opacity: 0, translateY: -navHeight }} // Animate exit for smooth hiding
        transition={{
          duration: 0.4, // Smooth transition for both opacity and position
          ease: [0.25, 0.1, 0.25, 1], // Custom easing for smooth effect
        }}
        style={{
          position: visibility?.isFixed ? "fixed" : "absolute",
          top: 0,
          minHeight: "70px",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: DefaultLayoutZIndex, // Default MUI z-index for AppBar
          backgroundColor: palette?.primary?.main,
        }}
      >
        <Toolbar
          sx={{
            color: palette?.white?.main,
            width: "100%",

            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Logo
            src={"./logo5.png"}
            width={`${width * 0.15}px`}
            height={`${navHeight}px`}
            style={{ minWidth: "8rem", borderRadius: null, border: null }}
          />
          <Box sx={{ ml: "auto" }} />
          {!isMobileView ? (
            <MenuList
              istop={true}
              isExpanded={true}
              isIcon={isIcon}
              width={width * 0.7}
            />
          ) : null}
        </Toolbar>
      </MotionAppBar>

      <AnimatedBox
        sx={{
          width: "100%",
          overflow: "hidden",
          height: `${ navHeight}px`,
        }}
     
      />
    </Box>
  );
};

export default MainComponent;
